import { Component, ElementRef, EventEmitter, Inject, Input, Output, PLATFORM_ID, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-ssr-rich-text-editor',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './ssr-safe-rich-text-editor.component.html',
  styleUrls: ['./ssr-safe-rich-text-editor.component.scss']
})
export class SsrSafeRichTextEditorComponent {

  @ViewChild('editor', { static: false })
  editor!: ElementRef<HTMLElement>;

  showEditor: boolean = false;

  @Input() isPost: boolean = false;
  @Input() set value(html: string) {
    if (!this.isBrowser || !this.editor) return;

    const el = this.editor.nativeElement;
    if (el.innerHTML !== html) {
      el.innerHTML = html || '';
      setTimeout(() => this.placeCursorAtEnd());
    }
  }

  @Output() valueChange = new EventEmitter<string>();
  @Output() exit = new EventEmitter<string>();

  private savedRange: Range | null = null;
  readonly isBrowser: boolean;

  fontMap: Record<string, string> = { '1': '10px', '2': '12px', '3': '14px', '4': '16px', '5': '18px', '6': '20px', '7': '22px' };
  selectedFontSize: string = '';

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  saveSelection() {
    if (!this.isBrowser) return;

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      this.savedRange = sel.getRangeAt(0).cloneRange();
    }
  }

  restoreSelection() {
    if (!this.isBrowser || !this.savedRange) return;

    const sel = window.getSelection();
    if (!sel) return;

    sel.removeAllRanges();
    sel.addRange(this.savedRange);
  }

  keepSelection(event: MouseEvent) {
    if (!this.isBrowser) return;
    event.preventDefault();
    this.saveSelection();
  }

  exec(command: string) {
    if (!this.isBrowser) return;
    this.restoreSelection();
    document.execCommand(command);
    this.saveSelection();
  }

  applyFontSize(event: Event) {
    if (!this.isBrowser) return;

    const select = event.target as HTMLSelectElement;
    const sizeKey = select.value;
    this.selectedFontSize = sizeKey;

    const sizeValue = this.fontMap[sizeKey];
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    if (range.collapsed) return;

    const span = document.createElement('span');
    span.style.fontSize = sizeValue;
    span.textContent = range.toString();

    range.deleteContents();
    range.insertNode(span);

    this.saveSelection();
  }

  insertLink() {
    if (!this.isBrowser) return;

    this.restoreSelection();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    let node = sel.anchorNode as HTMLElement;

    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement!;
    }

    const existingAnchor = node.closest('a') as HTMLAnchorElement | null;

    const existingUrl = existingAnchor?.getAttribute('href') || '';

    const url = prompt('Enter link URL', existingUrl);

    if (url === null) return;

    if (url.trim() === '') {
      if (existingAnchor) {
        document.execCommand('unlink');
      }
      return;
    }

    if (existingAnchor) {
      existingAnchor.setAttribute('href', url);
      existingAnchor.classList.add('rte-link');
      existingAnchor.setAttribute('target', '_blank');
      existingAnchor.setAttribute('rel', 'noopener noreferrer');
    } else {
      document.execCommand('createLink', false, url);

      const anchor = sel.anchorNode?.parentElement;
      if (anchor?.tagName === 'A') {
        anchor.classList.add('rte-link');
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
      }
    }
    this.valueChange.emit(this.editor.nativeElement.innerHTML);
    this.saveSelection();
  }

  removeLink() {
    if (!this.isBrowser) return;

    this.restoreSelection();
    document.execCommand('unlink', false, "");
    this.saveSelection();
  }

  emitChange() {
    if (!this.isBrowser) return;
    this.valueChange.emit(this.editor.nativeElement.innerHTML);
  }

  onBlur() {
    if (!this.isBrowser) return;
    this.exit.emit(this.editor.nativeElement.innerHTML);
  }

  placeCursorAtEnd() {
    if (!this.isBrowser) return;

    const el = this.editor.nativeElement;
    el.focus({ preventScroll: true });

    const range = document.createRange();
    const sel = window.getSelection();

    range.selectNodeContents(el);
    range.collapse(false);

    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  toggleEditor() {
    this.showEditor = !this.showEditor;
  }
}
